
import React from 'react'
import { CheckCircle, BookOpen, ArrowRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { useRecentModules } from '@/hooks/useRecentModules'

const LearnCard = () => {
  const navigate = useNavigate()
  const { recentModules, loading, addRecentModule } = useRecentModules()

  const handleModuleClick = (module: any) => {
    // Track the module access
    addRecentModule({
      id: module.id,
      title: module.title,
      description: module.description,
      category: module.category
    })
    
    // Fix: Use correct path for module detail
    navigate(`/learn/module/${module.id}`)
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 flex-shrink-0">
        <CardTitle className="text-base font-medium">Recent Learning</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/learn')}
          className="h-8 px-2"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-2 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          ) : recentModules.length > 0 ? (
            recentModules.map((module) => {
              const getModuleColors = (category: string) => {
                switch (category) {
                  case 'Wealth':
                    return {
                      background: 'bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100',
                      border: 'border-green-100',
                      iconBg: 'bg-green-100',
                      iconColor: 'text-green-600'
                    }
                  case 'Self':
                    return {
                      background: 'bg-gradient-to-r from-blue-50 to-sky-50 hover:from-blue-100 hover:to-sky-100',
                      border: 'border-blue-100',
                      iconBg: 'bg-blue-100',
                      iconColor: 'text-blue-600'
                    }
                  case 'Social':
                    return {
                      background: 'bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100',
                      border: 'border-orange-100',
                      iconBg: 'bg-orange-100',
                      iconColor: 'text-orange-600'
                    }
                  default:
                    return {
                      background: 'bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100',
                      border: 'border-gray-100',
                      iconBg: 'bg-gray-100',
                      iconColor: 'text-gray-600'
                    }
                }
              }

              const colors = getModuleColors(module.category)

              return (
                <div 
                  key={module.id} 
                  className={`flex items-center gap-3 p-3 ${colors.background} rounded-lg border ${colors.border} cursor-pointer transition-colors`}
                  onClick={() => handleModuleClick(module)}
                >
                  <div className={`w-8 h-8 ${colors.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <BookOpen className={`w-4 h-4 ${colors.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 text-sm break-words leading-relaxed">{module.title}</h3>
                    <p className="text-xs text-gray-600">{module.category}</p>
                  </div>
                  <CheckCircle className={`w-4 h-4 ${colors.iconColor} flex-shrink-0`} />
                </div>
              )
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-gray-500 mb-4">No modules accessed yet</p>
            </div>
          )}
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-3 flex-shrink-0"
          onClick={() => navigate('/learn')}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Browse Modules
        </Button>
      </CardContent>
    </Card>
  )
}

export default LearnCard
